
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import {
	PermissionsGroup,
} from '@/store/models.def';
import uuid from 'uuid';
import { StateHistory } from '@/util/stateHistory';
import accountx from '@/store/modules/accountx';

@Component({
	components: {},
})
export default class BaseCodedWidget extends Vue {
	public get permissionIds(): PermissionsGroup[] {
		return [];
	}
	public get expensiveHook(): string {
		return '';
	}

	public currentJobId: string = '';
	public loading = true;
	public loadingText = '';
	private _loadingTimer: number | null = null;

	public beforeDestroy() {
		this.currentJobId = '';
		if (this._loadingTimer !== null) {
			clearInterval(this._loadingTimer);
			this._loadingTimer = null;
		}
	}
	@Watch('expensiveHook', { immediate: true })
	public async expensiveTriggered() {
		try {
			const jobId = (this.currentJobId = uuid.v4());
			if (await this.loadHistory()) {
				this.currentJobId = '';
				this.loading = false;
				return;
			}
			this.checkInterrupted(jobId);

			// check permission
			if (this.permissionIds.length > 0 && !accountx.myPermissions) {
				return;
			}
			const myPermissions = accountx.myPermissions!;
			for (const perm of this.permissionIds) {
				if (!myPermissions[perm]) {
					return;
				}
			}

			this.loading = true;
			this.generateLoadingText(0);
			await this.expensiveCalc();
			this.loading = false;
		} catch (e) {
			if ((e as Error).message !== 'Interrupted') {
				console.error(e);
				this.loading = false;
			}
		}
	}

	public async expensiveCalc() {}

	public loadHistory() {
		const tag = (this.$options as any)._parentVnode.tag;
		const url = this.$route.path;
		const state = StateHistory.loadState(tag, url, this.expensiveHook);
		if (state) {
			for (const key in state) {
				if (state.hasOwnProperty(key)) {
					const data = state[key];
					this[key] = data;
				}
			}
			return true;
		}
		return false;
	}

	public saveHistory(...keys: string[]) {
		const tag = (this.$options as any)._parentVnode.tag;
		const url = this.$route.path;
		const state: any = {};
		for (const key of keys) {
			state[key] = this[key];
		}
		StateHistory.saveState(state, tag, url, this.expensiveHook);
	}

	/**
	 * Generate loading text with optional fake increment animation.
	 * @param ratio - Current progress ratio (0-1)
	 * @param maxRatio - Optional max ratio to animate to. Defaults to ratio + 0.19 (19%)
	 * Animation runs over 5 seconds with exponential slowdown.
	 */
	public generateLoadingText(ratio: number, maxRatio?: number) {
		// Clear any existing timer
		if (this._loadingTimer !== null) {
			clearInterval(this._loadingTimer);
			this._loadingTimer = null;
		}

		// Set the max ratio - defaults to ratio + 0.19 (19%)
		const max = maxRatio !== undefined ? maxRatio : ratio + 0.19;

		// If ratio >= max, just set the text directly
		if (ratio >= max) {
			this.loadingText = `${(ratio * 100).toFixed(1)}%`;
			return;
		}

		// Animate from ratio to max over 5 seconds with exponential slowdown
		const interval = 50; // update every 50ms
		// Exponential decay factor - moves 8% of remaining distance each tick
		// After 5 seconds (100 ticks): 1 - 0.92^100 ≈ 99.97% of the way there
		const decayFactor = 0.08;

		let currentRatio = ratio;
		this.loadingText = `${(currentRatio * 100).toFixed(1)}%`;

		this._loadingTimer = window.setInterval(() => {
			// Move a fraction of the remaining distance (exponential slowdown)
			const remaining = max - currentRatio;
			currentRatio += remaining * decayFactor;

			// Stop when we're very close to max (within 0.05%)
			if (max - currentRatio < 0.0005) {
				currentRatio = max;
				if (this._loadingTimer !== null) {
					clearInterval(this._loadingTimer);
					this._loadingTimer = null;
				}
			}
			this.loadingText = `${(currentRatio * 100).toFixed(1)}%`;
		}, interval);
	}
	public checkInterrupted(jobId: string) {
		if (this.currentJobId !== jobId) {
			throw Error('Interrupted');
		}
	}

}
